import APIService from 'api/APIService'
import { useSettings } from 'core/hooks/settings/useSettings'
import useNotifications from 'core/hooks/ui/useNotifications'
import useLocalStorage from 'core/hooks/utils/useLocalStorage'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import { JobStatus } from 'types/misc'

const SALES_DEMO_ERROR_MSG =
  'There was an error reseting this demo account, contact enginering team ASAP'

const useSalesDemoSandbox = () => {
  const { data: settings, set } = useSettings()
  const notification = useNotifications()
  const [resetDemoAccountJobId, setResetDemoAccountJobId] =
    useLocalStorage<string>('sales_demo_reset_job_id', '')
  const queryClient = useQueryClient()
  const jobID =
    resetDemoAccountJobId || settings.pollingJobs.resetDemoAccountJobId

  const mutationQuery = useMutation({
    mutationFn: APIService.sales.cleanData,
    onSuccess(data) {
      if (data.result === 'success') {
        set('pollingJobs', {
          ...settings.pollingJobs,
          resetDemoAccountJobId: data.job_token_id
        })
        setResetDemoAccountJobId(data.job_token_id)
        notification.success({
          title: `Demo account is being reset. This can take up to 3 minutes.`
        })
      }
    },
    onError() {
      notification.error({
        title: SALES_DEMO_ERROR_MSG
      })
    }
  })

  const loadingNotificationId = `sales-demo-${jobID}`

  useQueryCallbacks({
    queryKey: ['sales', 'demo', jobID],
    queryFn: () =>
      APIService.misc.getJobStatus({
        jobId: jobID
      }),
    enabled: !!jobID,
    refetchInterval: 2000,
    onSuccess: (response: JobStatus) => {
      const errors = response?.error_list ?? []
      const hasErrors = errors.length > 0
      if (response.status === 'finished' && !hasErrors) {
        queryClient.invalidateQueries({ queryKey: [] })
        setResetDemoAccountJobId('')

        notification.success({
          title: `Your demo account has been reset succesfully`,
          id: jobID
        })

        set('pollingJobs', {
          ...settings.pollingJobs,
          resetDemoAccountJobId: ''
        })

        notification.dismiss(loadingNotificationId)
      }

      if (
        (response.status === 'in_progress' || response.status === 'enqueued') &&
        !hasErrors
      ) {
        notification.loading({
          id: loadingNotificationId,
          position: 'bottom-left',
          title: 'Reseting demo data...',
          subtitle: 'This may take a few minutes.',
          duration: Infinity
        })
      }

      if (
        response.status === 'failed' ||
        (response.status === 'finished' && hasErrors)
      ) {
        notification.dismiss(loadingNotificationId)
        notification.error({
          title: SALES_DEMO_ERROR_MSG,
          id: jobID
        })
        set('pollingJobs', {
          ...settings.pollingJobs,
          resetDemoAccountJobId: ''
        })
        setResetDemoAccountJobId('')
      }
    },
    onError: () => {
      notification.dismiss(loadingNotificationId)
      notification.error({
        title: SALES_DEMO_ERROR_MSG,
        id: jobID
      })
      set('pollingJobs', {
        ...settings.pollingJobs,
        resetDemoAccountJobId: ''
      })
      setResetDemoAccountJobId('')
    }
  })

  const isWaitingJob = !!resetDemoAccountJobId || mutationQuery.isPending

  return {
    isWaitingJob,
    mutationQuery
  }
}

export default useSalesDemoSandbox
